/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { ReactNode } from 'react';

import { COLORS, FONT } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { CONTROL_HEIGHT_PX } from './progressAltMetrics';
import { FOCUS_RING_STYLE } from './progressAltStyles';

const CONTROL_STYLE = css`
  label: PROGRESS_ALT_SEGMENTED_CONTROL_STYLE;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const LEGEND_STYLE = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.midtoneFaded};
`;

const OPTIONS_STYLE = css`
  box-sizing: border-box;
  height: ${CONTROL_HEIGHT_PX}px;
  display: inline-flex;
  align-items: stretch;
  border: 1px solid ${COLORS.stroke};
  border-radius: 0;
  background: ${COLORS.white};
`;

/** Centred by flex rather than sat on padding, so every option is the group's height exactly. */
const OPTION_STYLE = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border: 0;
  border-left: 1px solid ${COLORS.stroke};
  background: transparent;
  color: ${COLORS.midtone};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:first-of-type {
    border-left: 0;
  }

  &:hover {
    background: ${COLORS.surfaceMuted};
    color: ${COLORS.ink};
  }

  ${FOCUS_RING_STYLE}
`;

const OPTION_SELECTED_STYLE = css`
  background: ${COLORS.ink};
  color: ${COLORS.white};
  cursor: default;

  &:hover {
    background: ${COLORS.ink};
    color: ${COLORS.white};
  }
`;

export type ProgressAltSegment<Id extends string> = {
  id: Id;
  /** Names the option. Read aloud, and shown as the button's text unless `content` stands in for it. */
  label: string;
  /** An icon to wear instead of the label, for options that read faster as a picture than a word. */
  content?: ReactNode;
};

/**
 * The Progress Alt toolbar's one control shape: a row of mutually exclusive options with the chosen
 * one inked in. Both toolbar pickers wear it, so they stay identical sitting side by side.
 */
export const ProgressAltSegmentedControl = <Id extends string>({
  legend,
  ariaLabel,
  segments,
  selectedId,
  onSelect,
}: {
  legend?: string;
  ariaLabel: string;
  segments: readonly ProgressAltSegment<Id>[];
  selectedId: Id;
  onSelect: (id: Id) => void;
}) => (
  <span css={CONTROL_STYLE}>
    {legend ? <span css={LEGEND_STYLE}>{legend}</span> : null}
    <span css={OPTIONS_STYLE} role='group' aria-label={ariaLabel}>
      {segments.map(segment => {
        const selected = segment.id === selectedId;
        return (
          <button
            key={segment.id}
            type='button'
            css={[OPTION_STYLE, selected && OPTION_SELECTED_STYLE]}
            aria-label={segment.content ? segment.label : undefined}
            aria-pressed={selected}
            onClick={selected ? undefined : () => onSelect(segment.id)}
          >
            {segment.content ?? segment.label}
          </button>
        );
      })}
    </span>
  </span>
);
